import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Users, TrendingUp, Clock, AlertTriangle, CheckCircle2, ArrowRight,
  Building2, MapPin, ClipboardList, RotateCcw, Zap, Search, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  read_only: { label: "Read-Only", color: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  frozen: { label: "Frozen", color: "bg-red-100 text-red-800", dot: "bg-red-500" },
  converted: { label: "Converted", color: "bg-cyan-100 text-cyan-800", dot: "bg-cyan-500" },
};

function FunnelCard({ icon: Icon, label, value, color, sub }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <span className="text-3xl font-extrabold text-slate-900">{value}</span>
        </div>
        <div className="text-sm font-semibold text-slate-600">{label}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function EngagementBar({ score }) {
  const color = score >= 75 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-2 w-28">
      <div className="flex-1 bg-slate-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 w-8 text-right">{score}%</span>
    </div>
  );
}

export default function TrialAdminDashboard() {
  const { api, user } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [converting, setConverting] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") fetchData();
  }, []);

  // Only Admin can access this page
  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-sm text-slate-500">The Trial Conversion Dashboard is only available to platform administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const [funnelRes, orgsRes] = await Promise.all([
        api.get("/admin/trial/funnel"),
        api.get("/admin/trial/orgs"),
      ]);
      setFunnel(funnelRes.data);
      setOrgs(orgsRes.data);
    } catch (err) {
      toast.error("Failed to load trial data");
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async (orgId, orgName) => {
    if (!window.confirm(`Convert "${orgName}" to paid plan?`)) return;
    setConverting(orgId);
    try {
      await api.post(`/admin/trial/orgs/${orgId}/convert`);
      toast.success(`${orgName} converted to paid plan`);
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to convert"));
    } finally {
      setConverting(null);
    }
  };

  const filtered = orgs.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (search && !o.name.toLowerCase().includes(search.toLowerCase()) && !o.code.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="trial-admin-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trial-admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Trial Conversion Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Monitor trial signups, engagement, and conversion funnel</p>
      </div>

      {/* Funnel Cards */}
      {funnel && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="trial-funnel-cards">
          <FunnelCard icon={Users} label="Total Signups" value={funnel.total_trial_signups} color="bg-slate-100 text-slate-600" />
          <FunnelCard icon={Zap} label="Active Trials" value={funnel.active_trials} color="bg-emerald-100 text-emerald-600" sub={`${TRIAL_DAYS} day window`} />
          <FunnelCard icon={Clock} label="Read-Only" value={funnel.read_only_grace} color="bg-amber-100 text-amber-600" sub="Grace period" />
          <FunnelCard icon={AlertTriangle} label="Frozen" value={funnel.frozen} color="bg-red-100 text-red-600" sub="Data retained" />
          <FunnelCard icon={CheckCircle2} label="Converted" value={funnel.converted_to_paid} color="bg-cyan-100 text-cyan-600" />
          <FunnelCard icon={TrendingUp} label="Conv. Rate" value={`${funnel.conversion_rate}%`} color="bg-violet-100 text-violet-600" />
          <FunnelCard icon={AlertTriangle} label="Expired" value={funnel.expired_trials} color="bg-slate-100 text-slate-500" sub="Total expired" />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="trial-admin-search"
            type="text"
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
          />
        </div>
        <div className="flex gap-1.5">
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "read_only", label: "Read-Only" },
            { key: "frozen", label: "Frozen" },
            { key: "converted", label: "Converted" },
          ].map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                filter === f.key
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Organizations Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Trial Organizations ({filtered.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trial-orgs-table">
              <thead>
                <tr className="bg-slate-50 text-left border-b border-slate-200">
                  <th className="px-4 py-3 font-semibold text-slate-600">Organization</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">Days</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">
                    <div className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Users</div>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600">
                    <div className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Depts</div>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600">
                    <div className="flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> BIA</div>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600">
                    <div className="flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Plans</div>
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600">AI Used</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">Engagement</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                      No organizations match the current filter.
                    </td>
                  </tr>
                ) : (
                  filtered.map((org) => {
                    const sc = STATUS_CONFIG[org.status] || STATUS_CONFIG.active;
                    return (
                      <tr key={org.id} className="hover:bg-slate-50/50 transition" data-testid={`trial-org-row-${org.code}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-800">{org.name}</div>
                          <div className="text-xs text-slate-400">{org.code} &middot; {org.industry}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${sc.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                            {sc.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {org.status === "converted" ? (
                            <span className="text-xs text-cyan-600 font-semibold">Paid</span>
                          ) : (
                            <div>
                              <div className="font-semibold text-slate-800">{org.days_remaining}</div>
                              <div className="text-xs text-slate-400">remaining</div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-700">{org.usage.users}</td>
                        <td className="px-4 py-3 font-medium text-slate-700">{org.usage.departments}</td>
                        <td className="px-4 py-3 font-medium text-slate-700">{org.usage.bia_processes}</td>
                        <td className="px-4 py-3 font-medium text-slate-700">{org.usage.recovery_plans}</td>
                        <td className="px-4 py-3 font-medium text-slate-700">{org.ai_queries_used}</td>
                        <td className="px-4 py-3"><EngagementBar score={org.engagement_score} /></td>
                        <td className="px-4 py-3">
                          {org.status !== "converted" && (
                            <button
                              data-testid={`convert-btn-${org.code}`}
                              onClick={() => handleConvert(org.id, org.name)}
                              disabled={converting === org.id}
                              className="inline-flex items-center gap-1 bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition disabled:opacity-50"
                            >
                              {converting === org.id ? "..." : "Convert"}
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const TRIAL_DAYS = 14;
